import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Camera from "@insite/mobius/Icons/Camera";
import Facebook from "@insite/mobius/Icons/Facebook";
import FileText from "@insite/mobius/Icons/FileText";
import Instagram from "@insite/mobius/Icons/Instagram";
import Linkedin from "@insite/mobius/Icons/Linkedin";
import Rss from "@insite/mobius/Icons/Rss";
import Twitter from "@insite/mobius/Icons/Twitter";
import User from "@insite/mobius/Icons/User";
import Video from "@insite/mobius/Icons/Video";
import Youtube from "@insite/mobius/Icons/Youtube";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pinterest from "@tSurya/Icons/Pinterest";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import { isThemeActive, THEME_IDS } from "@tSurya/theme/themeHelper";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const isMGBW = isThemeActive(THEME_IDS.MGBW);

const enum fields {
    heading = "heading",
    tittle = "tittle",
    direction = "direction",
    alignment = "alignment",
    iconsContainerAlignment = "iconsContainerAlignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    visibilityState = "visibilityState",
    iconSize = "iconSize",
    iconColor = "iconColor",
    backgroundColor = "backgroundColor",
    linksPerRow = "linksPerRow",
    customCSS = "customCSS",
}

const enum icons {
    instagram = "Instagram",
    facebook = "Facebook",
    youtube = "Youtube",
    twitter = "Twitter",
    pinterest = "Pinterest",
    rss = "Rss",
    user = "User",
    linkedin = "Linkedin",
    camera = "Camera",
    video = "Video",
    notebook = "Notebook",
}

const enum visibilityState {
    both = "both",
    label = "label",
    icon = "icon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.title]: string;
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "right";
        [fields.iconsContainerAlignment]: "flex-start" | "flex-end" | "center";
        [fields.visibilityState]: visibilityState;
        [fields.title]: string;
        [fields.iconSize]: number;
        [fields.linksPerRow]: number;
        [fields.iconColor]: string;
        [fields.backgroundColor]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
}

interface LinkModel {
    fields: {
        title: string;
        icon: string;
        openInNewWindow: boolean;
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

export interface LinkListStyles {
    socialLinkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: InjectableCss;
    container?: InjectableCss;
    header?: LinkPresentationProps;
    title?: InjectableCss;
}

export const linkListStyles: LinkListStyles = {
    socialLinkListWrapper: {
        css: css`
            width: 100%;
            padding: 10px 0 14px;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
            column-gap: 15px;
            ${isMGBW && css`
                padding: 0;
                column-gap: 18px;
                margin-top:16px;
            `}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    isMGBW ? css`
                        padding-top: 0;
                        padding-bottom: 0;
                        margin-bottom: 16px;
                    ` : css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    isMGBW ? css`
                        padding-top: 0;
                        padding-bottom: 0;
                        margin-bottom: 16px;
                    ` : css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    isMGBW ? css`
                        padding-top: 0;
                        padding-bottom: 0;
                        margin-bottom: 16px;
                    ` : css`
                        padding-top: 0;
                        padding-bottom: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 18px 0 0;
            margin-right: 0;
            flex-shrink: 0;
        `,
    },
    container: {
        css: css`
            margin-top: 33px;
            margin-bottom: 10px;
            ${isMGBW && css`
                margin-top: 0;
                margin-bottom: 16px;
            `}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 0px;
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-top: 0px;
                        margin-bottom: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    header: {
        css: css`
            cursor: default;
            &:hover {
                span[class^="TypographyStyle"] {
                    color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
                    cursor: default;
                }
            }
            span[class^="TypographyStyle"] {
                font-family: ${TYPOGRAPHY.footerHeadings.fontFamily};
                font-weight: ${TYPOGRAPHY.footerHeadings.weight};
                font-size: ${TYPOGRAPHY.footerHeadings.size};
                line-height: ${TYPOGRAPHY.footerHeadings.lineHeight};
                text-transform: ${TYPOGRAPHY.footerHeadings.textTransform};
                cursor: default;
                color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
                margin-bottom: ${isMGBW ? "0" : "20px"};
                ${isMGBW ? `letter-spacing: ${(TYPOGRAPHY.footerHeadings as any).letterSpacing};` : ""}

                ${isThemeActive(THEME_IDS.GLOBAL_VIEWS) ? `font-style: italic;` : `font-style: normal;`}

                &:hover {
                    color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-bottom: 9px;
                        `,
                        css`
                            margin-bottom: 9px;
                        `,
                        css`
                            margin-bottom: 9px;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    title: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${isMGBW ? COLORS.text.main : getColor("primary.contrast")};
            margin-bottom: 27px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 13px;
                    `,
                    css`
                        margin-bottom: 13px;
                    `,
                    css`
                        margin-bottom: 13px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

export const socialLinksStyles = linkListStyles;

const GetIcon = (icon: string) => {
    switch (icon) {
        case icons.youtube:
            return Youtube;
        case icons.twitter:
            return Twitter;
        case icons.facebook:
            return Facebook;
        case icons.pinterest:
            return Pinterest;
        case icons.instagram:
            return Instagram;
        case icons.linkedin:
            return Linkedin;
        case icons.notebook:
            return FileText;
        case icons.rss:
            return Rss;
        case icons.user:
            return User;
        case icons.video:
            return Video;
        case icons.camera:
            return Camera;
        default:
            break;
    }

    return "";
};
export const FooterSocialLinks: FC<OwnProps> = ({ fields }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination).links;

    const styles = useMergeStyles("socialLinks", linkListStyles);

    const customCssWrapper = {
        css: css`
            margin-bottom: 20px;
            ${fields.customCSS}
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 8px;
                    `,
                    css`
                        margin-bottom: 8px;
                    `,
                    css`
                        margin-bottom: 8px;
                    `,
                    null,
                    null,
                ])};
        `,
    };
    const socialLinkListStyle = css`
        ${fields.direction === "horizontal" ? "flex-direction: row;" : "flex-direction: column;"}
        /* ${fields.backgroundColor && `background-color: ${fields.backgroundColor};`} */
        ${styles.socialLinkListWrapper?.css}
        svg path {
            fill: ${fields.backgroundColor};
        }
        svg {
            stroke: ${fields.iconColor};
        }
        justify-content: ${fields.iconsContainerAlignment};
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    padding: 0px !important;
                `,
                css``,
                css``,
                css``,
                css``,
            ])};
        > .link-wrapper:first-child {
            > a > span:first-child {
                margin-left: 0;
            }
        }
    `;

    const linkWrapperStyle = css`
        padding-right: 0px !important;
        margin-right: 15px;
        ${styles.linkWrapper?.css}
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    padding-right: 0px !important;
                `,
                css``,
                css``,
                css``,
                css``,
            ])};
    `;

    const showIcon = fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.icon;
    const showLabel =
        fields.visibilityState === visibilityState.both || fields.visibilityState === visibilityState.label;

    return (
        <StyledWrapper {...customCssWrapper}>
            {fields.heading || fields.title ? (
                <StyledWrapper {...styles.container}>
                    {fields.heading ? (
                        <Link {...styles.header} aria-label={fields.heading || "socialHeading"}>
                            {fields.heading}
                        </Link>
                    ) : null}
                    {fields.title ? <StyledWrapper {...styles.title}>{fields.title}</StyledWrapper> : null}
                </StyledWrapper>
            ) : null}

            <StyledWrapper className="social-links" css={socialLinkListStyle}>
                {links.map((link, index) => (
                    <StyledWrapper
                        className="link-wrapper"
                        css={linkWrapperStyle}
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                    >
                        {link.url && (
                            <Link
                                className="social-link"
                                {...styles.link}
                                href={link.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                aria-label={fields.links[index].fields.title || "socialIcon"}
                                icon={{
                                    iconProps: {
                                        size: fields.iconSize,
                                        color: fields.iconColor,
                                        css: css`
                                            margin: 0;
                                        `,
                                        src: showIcon ? GetIcon(fields.links[index].fields.icon) : undefined,
                                        title: fields.links[index].fields.title,
                                    },
                                    position: fields.alignment,
                                }}
                            >
                                {showLabel && fields.links[index].fields.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.social-links{
}

.link-wrapper{
}

.social-link{
}`;

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 2,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 3,
};

const definition: WidgetDefinition = {
    group: "Basic",
    icon: "thumbs-up",
    isSystem: true,
    fieldDefinitions: [
        {
            name: fields.heading,
            displayName: "Heading",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
            // isRequired: true,
            sortOrder: 0,
        },
        {
            name: fields.title,
            displayName: "Tittle",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tooltip: "text tip",
            placeholder: "text placeholder",
            // isRequired: true,
            sortOrder: 0,
        },
        {
            name: fields.links,
            editorTemplate: "ListField",
            tab: contentTab,
            getDisplay: (item: HasFields) => {
                return item.fields.title;
            },
            defaultValue: [],
            fieldType: "Translatable",
            fieldDefinitions: [
                {
                    name: "title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "icon",
                    displayName: "Select Icon",
                    editorTemplate: "DropDownField",
                    options: [
                        { displayName: "Instagram", value: icons.instagram },
                        { displayName: "Youtube", value: icons.youtube },
                        { displayName: "Facebook", value: icons.facebook },
                        { displayName: "Pinterest", value: icons.pinterest },
                        { displayName: "Twitter", value: icons.twitter },
                        { displayName: "Rss", value: icons.rss },
                        { displayName: "User", value: icons.user },
                        { displayName: "Linkedin", value: icons.linkedin },
                        { displayName: "Camera", value: icons.camera },
                        { displayName: "Video", value: icons.video },
                        { displayName: "Notebook", value: icons.notebook },
                    ],
                    defaultValue: "",
                },
                {
                    name: "destination",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Url",
                    },
                    isRequired: true,
                },
                {
                    name: "openInNewWindow",
                    editorTemplate: "CheckboxField",
                    defaultValue: false,
                },
            ],
        },
        {
            name: fields.direction,
            displayName: "Display",
            editorTemplate: "RadioButtonsField",
            defaultValue: "horizontal",
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 1,
            options: [
                {
                    displayName: "Column",
                    value: "vertical",
                },
                {
                    displayName: "Row",
                    value: "horizontal",
                },
            ],
        },
        {
            name: fields.alignment,
            displayName: "Text Position",
            editorTemplate: "RadioButtonsField",
            defaultValue: "left",
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 2,
            options: [
                {
                    displayName: "Left of icon",
                    value: "left",
                },
                {
                    displayName: "Right of icon",
                    value: "right",
                },
            ],
        },
        {
            name: fields.iconsContainerAlignment,
            displayName: "Aligment",
            editorTemplate: "RadioButtonsField",
            defaultValue: "flex-start",
            fieldType: "General",
            tab: settingsTab,
            sortOrder: 2,
            options: [
                {
                    displayName: "Algin Left",
                    value: "flex-start",
                },
                {
                    displayName: "Align Center",
                    value: "center",
                },
                {
                    displayName: "Algin Right",
                    value: "flex-end",
                },
            ],
        },
        {
            name: fields.visibilityState,
            displayName: "Visibility",
            editorTemplate: "RadioButtonsField",
            tab: settingsTab,
            options: [
                {
                    displayName: "Show Both",
                    value: visibilityState.both,
                },
                {
                    displayName: "Show Label Only",
                    value: visibilityState.label,
                },
                {
                    displayName: "Show Icon Only",
                    value: visibilityState.icon,
                },
            ],
            defaultValue: visibilityState.both,
            fieldType: "General",
            sortOrder: 4,
        },
        {
            name: fields.iconSize,
            displayName: "Icon Size",
            editorTemplate: "IntegerField",
            defaultValue: 30,
            min: 1,
            fieldType: "General",
            sortOrder: 3,
            tab: settingsTab,
        },
        {
            name: fields.linksPerRow,
            displayName: "Icons Per Row",
            fieldType: "General",
            editorTemplate: "DropDownField",
            options: [
                { value: 1 },
                { value: 2 },
                { value: 3 },
                { value: 4 },
                { value: 5 },
                { value: 6 },
                { value: 7 },
                { value: 8 },
            ],
            sortOrder: 3,
            defaultValue: 8,
            isVisible: (item?: HasFields) => {
                return item?.fields.direction === "horizontal";
            },
            tab: settingsTab,
        },
        {
            name: fields.iconColor,
            displayName: "Icon Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 0,
            tab: settingsTab,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            sortOrder: 1,
            tab: settingsTab,
        },
        {
            name: fields.customCSS,
            displayName: "Custom CSS",
            editorTemplate: "CodeField",
            fieldType: "General",
            tab: advancedTab,
            defaultValue: defaultCustomCss,
            isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            validate: value => {
                if (value === undefined || value === null) {
                    return "";
                }

                const result = cssLinter.parse(value, { silent: true });
                if (result?.stylesheet?.parsingErrors) {
                    // the error output at this time only has room for one line so we just show the first error
                    return result.stylesheet.parsingErrors.length <= 0
                        ? ""
                        : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                }

                return "Unable to parse Css";
            },
            options: {
                mode: "css",
                lint: true,
                autoRefresh: true,
            },
        },
    ],
};

const widgetModule: WidgetModule = {
    component: FooterSocialLinks,
    definition,
};

export default widgetModule;
